// A Inteligência pericial (fase C): perguntas que o perito faz sobre a própria operação, respondidas pelos
// dados dele. Portado de `src/lib/insights.ts` da Central do Perito, no commit f5c1923. PURO, com o relógio por
// parâmetro e prova própria (`tests/perito-relatorios.mjs`).
//
// O que muda de lá, e por quê:
//
//  - Os dias são os de São Paulo (`datas.ts`). Lá, `new Date("2026-11-01")` é meia-noite UTC — em Brasília, 21h
//    do dia 31 —, e o recebimento do dia 1 caía no mês anterior no gráfico.
//  - O período é de dias de CALENDÁRIO e termina HOJE: "30 dias" é hoje e os 29 dias antes, como no painel das
//    Avaliações. Lá ele não tinha fim, e a nomeação com data futura entrava no período.
//  - O EMPATE é dito. Lá o primeiro que entrou no mapa ganhava, e "o tribunal que mais me nomeia" podia ser um
//    de dois com o mesmo número, sem a tela dizer. Aqui o desempate é alfabético e a resposta traz os empatados.
//  - O ranking dos tribunais tem TODOS, e não os 8 primeiros: o corte escondia os demais sem avisar.
//  - A receita anualizada é a soma do MESMO gráfico de 12 meses (`serieMensal`, da fase B), e o recebido conta no
//    mês em que ENTROU. Lá eram duas contas: 365 dias corridos no cartão e 12 meses no gráfico, e os dois números
//    podiam discordar na mesma tela.
//  - O tempo entre o depósito e o recebimento é do período escolhido, como o resto do cartão; lá era de sempre,
//    sob um seletor de período que não o mudava.
//  - "Sem movimentação" olha a última mudança na perícia E o histórico dela: lá só `atualizadoEm`, que não muda
//    quando uma tarefa é concluída.
//  - Atrasada é a perícia ativa com a entrega do laudo antes de hoje. Lá também contava o status "vencido", que
//    aqui não existe: o status acompanha a etapa (000385).
import { diaDe, diasEntre, hoje, maisDias } from "./datas";
import { dataDoRecebimento, serieMensal } from "./financeiro";
import { etapaAtiva } from "./pipeline";
import type { HistoricoEntry, Pericia } from "./tipos";

export type PeriodoDaInteligencia = "30" | "90" | "ano";
export const PERIODOS_DA_INTELIGENCIA: readonly PeriodoDaInteligencia[] = ["30", "90", "ano"];
export const ROTULO_DO_PERIODO: Record<PeriodoDaInteligencia, string> = { "30": "Últimos 30 dias", "90": "Últimos 90 dias", ano: "Últimos 12 meses" };
const DIAS_DO_PERIODO: Record<PeriodoDaInteligencia, number> = { "30": 30, "90": 90, ano: 365 };

/** O período escolhido, ou o padrão (90 dias, como lá). */
export function periodoDaBusca(v: string | null): PeriodoDaInteligencia {
  return (PERIODOS_DA_INTELIGENCIA as readonly string[]).includes(v ?? "") ? (v as PeriodoDaInteligencia) : "90";
}

/** O primeiro e o último dia do período (AAAA-MM-DD), em São Paulo. */
export function janelaDoPeriodo(periodo: PeriodoDaInteligencia, agora: Date = new Date()): { de: string; ate: string } {
  const ate = hoje(agora);
  return { de: maisDias(ate, -(DIAS_DO_PERIODO[periodo] - 1)), ate };
}

/** Quem lidera, os empatados com ele e o valor. Nulo quando não há nada acima de zero. */
export interface Lider {
  nome: string;
  valor: number;
  empatados: string[];
}

/** O maior de um mapa; o desempate é alfabético, e os empatados vêm junto. Dinheiro empata ao centavo. */
export function liderDe(mapa: Map<string, number>): Lider | null {
  const igual = (a: number, b: number) => Math.round(a * 100) === Math.round(b * 100);
  const lista = [...mapa.entries()].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "pt-BR"));
  if (lista.length === 0) return null;
  const [nome, valor] = lista[0];
  return { nome, valor, empatados: lista.slice(1).filter(([, v]) => igual(v, valor)).map(([n]) => n) };
}

/** O dia que situa a perícia no período: a nomeação e, sem ela, o cadastro (como lá). */
export function diaDaPericia(p: Pericia): string | null {
  return diaDe(p.dataNomeacao) ?? diaDe(p.criadoEm);
}

/** A última movimentação da perícia: a última mudança nela ou no histórico dela, o que vier depois. */
export function ultimaMovimentacao(p: Pericia, historico: HistoricoEntry[]): string | null {
  const datas = [p.atualizadoEm, ...historico.filter((h) => h.pericia_id === p.id).map((h) => h.ts)].map(diaDe).filter((d): d is string => !!d);
  return datas.length ? datas.sort().at(-1)! : null;
}

export interface Inteligencia {
  janela: { de: string; ate: string };
  pericias: number;
  tribunalMaisNomeia: Lider | null;
  tribunalMaisReceita: Lider | null;
  varaMaisLucrativa: Lider | null;
  tipoMaisRentavel: Lider | null;
  /** Dias entre o depósito (o vencimento) e o recebimento, em média, dos recebimentos do período. */
  diasAteReceber: { dias: number; amostra: number } | null;
  recebidoPorCaso: { valor: number; casos: number } | null;
  receitaDe12Meses: { valor: number; de: string; ate: string };
  atrasadas: { pericias: Pericia[]; ativas: number };
  semMovimentacao: Pericia[];
  /** Todos os tribunais com nomeação no período, do que mais nomeia ao que menos. */
  rankingDeTribunais: Array<{ nome: string; quantas: number }>;
  /** Os 12 meses, do mais antigo ao de hoje, com o recebido que ENTROU em cada um. */
  recebimentosPorMes: Array<{ mes: string; valor: number }>;
}

const SEM_TRIBUNAL = "Sem tribunal";

export function calcularInteligencia(pericias: Pericia[], historico: HistoricoEntry[], periodo: PeriodoDaInteligencia, agora: Date = new Date()): Inteligencia {
  const janela = janelaDoPeriodo(periodo, agora);
  const dentro = (dia: string | null) => !!dia && dia >= janela.de && dia <= janela.ate;
  const doPeriodo = pericias.filter((p) => dentro(diaDaPericia(p)));

  const nomeacoes = new Map<string, number>();
  for (const p of doPeriodo) {
    const t = p.tribunal || SEM_TRIBUNAL;
    nomeacoes.set(t, (nomeacoes.get(t) ?? 0) + 1);
  }

  const receitaTribunal = new Map<string, number>();
  const receitaVara = new Map<string, number>();
  let recebidoNoPeriodo = 0;
  const casosComRecebimento = new Set<string>();
  let somaDias = 0;
  let amostra = 0;
  for (const p of pericias) {
    const tribunal = p.tribunal || SEM_TRIBUNAL;
    const vara = p.vara ? `${p.vara}${p.tribunal ? ` — ${p.tribunal}` : ""}` : null;
    for (const h of p.honorarios) {
      if (h.recebido <= 0 || !dentro(dataDoRecebimento(h))) continue;
      recebidoNoPeriodo += h.recebido;
      casosComRecebimento.add(p.id);
      receitaTribunal.set(tribunal, (receitaTribunal.get(tribunal) ?? 0) + h.recebido);
      // A perícia sem vara não entra na disputa da vara (lá ela competia como "Não informada").
      if (vara) receitaVara.set(vara, (receitaVara.get(vara) ?? 0) + h.recebido);
      const venc = diaDe(h.vencimento);
      const rec = diaDe(h.dataRecebimento);
      if (venc && rec) {
        somaDias += diasEntre(venc, rec);
        amostra += 1;
      }
    }
  }

  const porTipo = new Map<string, { soma: number; n: number }>();
  for (const p of doPeriodo) {
    const total = p.honorarios.reduce((a, h) => a + (h.valor || 0), 0);
    if (total <= 0) continue;
    const tipo = p.tipoPericia || "Sem tipo";
    const atual = porTipo.get(tipo) ?? { soma: 0, n: 0 };
    porTipo.set(tipo, { soma: atual.soma + total, n: atual.n + 1 });
  }
  const mediaPorTipo = new Map([...porTipo.entries()].map(([k, v]) => [k, v.soma / v.n]));

  const serie = serieMensal(pericias, 12, agora);
  const hojeSP = hoje(agora);
  const ativas = pericias.filter((p) => etapaAtiva(p));
  const atrasadas = ativas.filter((p) => {
    const entrega = diaDe(p.dataEntregaLaudo);
    return !!entrega && entrega < hojeSP;
  });
  const corte = maisDias(hojeSP, -30);
  const semMovimentacao = ativas.filter((p) => {
    const ultima = ultimaMovimentacao(p, historico);
    return !!ultima && ultima < corte;
  });
  const porNome = (a: Pericia, b: Pericia) => (a.apelido || a.numeroProcesso).localeCompare(b.apelido || b.numeroProcesso, "pt-BR");

  return {
    janela,
    pericias: doPeriodo.length,
    tribunalMaisNomeia: liderDe(nomeacoes),
    tribunalMaisReceita: liderDe(receitaTribunal),
    varaMaisLucrativa: liderDe(receitaVara),
    tipoMaisRentavel: liderDe(mediaPorTipo),
    diasAteReceber: amostra > 0 ? { dias: Math.round(somaDias / amostra), amostra } : null,
    recebidoPorCaso: casosComRecebimento.size > 0 ? { valor: recebidoNoPeriodo / casosComRecebimento.size, casos: casosComRecebimento.size } : null,
    receitaDe12Meses: { valor: serie.reduce((a, m) => a + m.recebido, 0), de: serie[0].mes, ate: serie[serie.length - 1].mes },
    atrasadas: { pericias: atrasadas.sort(porNome), ativas: ativas.length },
    semMovimentacao: semMovimentacao.sort(porNome),
    rankingDeTribunais: [...nomeacoes.entries()].map(([nome, quantas]) => ({ nome, quantas })).sort((a, b) => b.quantas - a.quantas || a.nome.localeCompare(b.nome, "pt-BR")),
    recebimentosPorMes: serie.map((m) => ({ mes: m.mes, valor: m.recebido })),
  };
}

/** O mês por extenso curto, "nov/26". */
export function rotuloDoMes(mes: string): string {
  const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const [a, m] = mes.split("-").map(Number);
  return `${nomes[m - 1]}/${String(a).slice(2)}`;
}
